import { AgentExecutionFlow } from '../types/index';
import { AgentType, AgentModelOverride } from '../types/models';
import { ApiService } from '../services/apiService';

/**
 * Maps agent type strings from execution steps to config keys.
 * Backend uses PascalCase with optional "Agent" suffix.
 */
function normalizeAgentType(agentType: string): AgentType | null {
  const normalized = agentType.replace(/Agent$/, '').toLowerCase();
  const mapping: Record<string, AgentType> = {
    orchestrator: 'orchestrator',
    researcher: 'researcher',
    reducer: 'reducer',
    writer: 'writer',
    summarizer: 'summarizer',
    relevancechecker: 'relevancechecker',
    relevance: 'relevancechecker',
  };
  return mapping[normalized] ?? null;
}

/**
 * Builds a reverse lookup from modelId to providerType using the available models API.
 */
async function buildModelToProviderMap(): Promise<Record<string, string>> {
  const apiService = ApiService.getInstance();
  const modelsData = await apiService.getAvailableModels();
  const map: Record<string, string> = {};
  for (const [providerType, models] of Object.entries(modelsData.modelsByProvider || {})) {
    for (const model of models) {
      map[model.modelId] = providerType;
    }
  }
  return map;
}

/**
 * Extracts a ProviderSelector-compatible config JSON from an execution flow.
 * Returns the JSON string ready for clipboard, or null if extraction fails.
 */
export async function extractConfigFromExecution(
  flowData: AgentExecutionFlow,
  executionId: string
): Promise<string | null> {
  if (!flowData.executionSteps || flowData.executionSteps.length === 0) {
    return null;
  }

  const modelToProvider = await buildModelToProviderMap();

  const config: Record<AgentType, AgentModelOverride | undefined> = {
    orchestrator: undefined,
    researcher: undefined,
    reducer: undefined,
    writer: undefined,
    summarizer: undefined,
    relevancechecker: undefined,
  };

  for (const step of flowData.executionSteps) {
    const agentKey = normalizeAgentType(step.agentType);
    if (!agentKey) continue;
    // Take the first occurrence per agent (skip duplicates from retries)
    if (config[agentKey]) continue;

    const providerType = step.modelUsed ? modelToProvider[step.modelUsed] : undefined;
    if (!step.modelUsed || !providerType) continue;

    const override: AgentModelOverride = {
      providerType,
      modelId: step.modelUsed,
    };
    if (step.temperature != null) override.temperature = step.temperature;
    if (step.topP != null) override.topP = step.topP;
    if (step.seed != null) override.seed = step.seed;
    if (step.frequencyPenalty != null) override.frequencyPenalty = step.frequencyPenalty;
    if (step.presencePenalty != null) override.presencePenalty = step.presencePenalty;
    if (step.systemPromptHash) override.systemPromptHash = step.systemPromptHash;

    config[agentKey] = override;
  }

  // Check we got at least one agent configured
  const hasAny = Object.values(config).some(v => v !== undefined);
  if (!hasAny) return null;

  const exportData = {
    version: 1,
    name: `Copied from ${executionId}`,
    config,
  };

  return JSON.stringify(exportData, null, 2);
}
